import { Autocomplete } from "@mui/material";
import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from "react-hook-form";
import TextField from "@mui/material/TextField";

export type AutocompleteOption = { label: string; value: string };

type Props<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: AutocompleteOption[];
  label: string;
  onChange?: (value: string) => void;
  error?: string;
  helperText?: string;
  disabled?: boolean;
};

export const AutocompleteWithControl = <T extends FieldValues>({
  name,
  control,
  options,
  label,
  onChange,
  error,
  helperText,
  disabled,
}: Props<T>) => {
  return (
    <Controller<T>
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const { ref, ...restField } = field;
        const selectedOption =
          options.find((opt) => opt.value === restField.value) || null;

        return (
          <Autocomplete<AutocompleteOption, false>
            {...restField}
            value={selectedOption}
            onChange={(_, option) => {
              restField.onChange(option?.value || "");
              if (onChange) onChange(option?.value || "");
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            disablePortal
            options={options}
            fullWidth
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                fullWidth
                error={!!fieldState.error?.message || !!error}
                helperText={fieldState.error?.message || error || helperText}
                inputRef={ref}
                inputProps={{
                  ...params.inputProps,
                  name: field.name,
                }}
              />
            )}
          />
        );
      }}
    />
  );
};
